/**
 * Picking the glyph a division or an equipment type wears —
 * `docs/marks-and-icons.md` §3, board 4.
 *
 * Aaron: "in the division section where you create new divisions, they already
 * select a color — let's have them select an icon. I like that it shows them a
 * sample of what it would look like." So the grid and the preview are one
 * component: picking is only half of it, and the half that matters is seeing
 * the tile the way a list row will draw it.
 *
 * One picker for both sets, and for whatever picks an icon next (the spec names
 * catalog item kinds and rental profiles). The set decides the glyphs; nothing
 * else about it changes.
 */
import { useMemo, useState } from 'react'
import { Glyph, glyphLabel, markInk, markTint, type GlyphSet } from '../../frame/mark-glyphs.js'
import { glyphSlugs } from '../../frame/mark-glyphs.js'
import { IconSearch } from '../../frame/icons.js'
import { cn } from '../../lib/cn.js'

/** Above this many, hunting beats scanning and the box earns its row (§3). */
const SEARCHABLE_FROM = 24

export interface IconPickerProps {
  set: GlyphSet
  value: string | null
  onChange: (slug: string) => void
  /** What the chosen tile is tinted with, so the two fields agree as you pick. */
  colour: string | null
  label: string
  /** `Field`'s own label, when there is one above — the pattern `ChipsField` set. */
  labelledBy?: string
}

export function IconPicker({
  set, value, onChange, colour, label, labelledBy,
}: IconPickerProps) {
  const [query, setQuery] = useState('')
  const slugs = glyphSlugs(set)
  const searchable = slugs.length > SEARCHABLE_FROM

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return slugs
    /* Both the slug and the label, because somebody types "mower" and somebody
       else types "zturn", and neither should come back empty. */
    return slugs.filter((slug) =>
      slug.includes(needle) || glyphLabel(set, slug).toLowerCase().includes(needle))
  }, [slugs, query, set])

  const ink = markInk(colour)

  return (
    <div className="flex flex-col gap-2">
      {searchable && (
        <div className="relative">
          <IconSearch
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint"
            size={14}
          />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${slugs.length} icons`}
            aria-label={`Search ${label.toLowerCase()}`}
            className="h-[30px] w-full rounded-[8px] border border-line bg-field pl-8 pr-2.5
                       text-[12.5px] placeholder:text-text-faint focus:outline-none
                       focus:ring-2 focus:ring-accent-ring"
          />
        </div>
      )}

      <div
        role="radiogroup"
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        /*
         * Eight per row, which is what board 4 draws and what makes 24 and 29
         * both land on tidy rows. `auto-fill` rather than a fixed eight so the
         * grid folds on a phone instead of scrolling sideways.
         */
        className="grid gap-1.5"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))' }}
      >
        {shown.map((slug) => {
          const chosen = slug === value
          return (
            <button
              key={slug}
              type="button"
              role="radio"
              aria-checked={chosen}
              aria-label={glyphLabel(set, slug)}
              title={glyphLabel(set, slug)}
              onClick={() => onChange(slug)}
              /* The chosen tile wears the colour that is being picked beside
                 it — the clearest way to show that the two fields make one
                 thing, and it is the tile the preview is about to draw. */
              style={chosen ? { background: markTint(colour), borderColor: ink, color: ink } : {}}
              className={cn(
                'flex h-[44px] items-center justify-center rounded-[10px] border',
                chosen
                  ? 'border-2 text-text'
                  : 'border-line bg-surface text-text-muted hover:border-line-strong hover:text-text',
                'focus:outline-none focus:ring-2 focus:ring-accent-ring',
              )}
            >
              <Glyph set={set} slug={slug} size={22} />
            </button>
          )
        })}
      </div>

      {shown.length === 0 && (
        <p className="py-2 text-[12.5px] text-text-muted">No icon matches “{query.trim()}”.</p>
      )}
    </div>
  )
}

/**
 * The three sizes a mark is drawn at, with the sentence board 4 puts beside it.
 *
 * It is not decoration: a glyph that reads at 56 can be a smudge at 28, and the
 * list row is the one that matters. Showing all three is the cheapest way to
 * find that out before saving rather than after.
 *
 * The status dot is deliberately absent and the sentence says so — this is the
 * division's tile, and the dot belongs to a job's status, which a division does
 * not have.
 */
export function MarkPreview({ set, slug, colour }: {
  set: GlyphSet
  slug: string | null
  colour: string | null
}) {
  return (
    <div className="flex items-center gap-4 rounded-[10px] bg-surface-alt px-3.5 py-3">
      <div className="flex items-end gap-2.5">
        {[28, 44, 56].map((size) => (
          slug
            ? <Tile key={size} set={set} slug={slug} colour={colour} size={size} />
            /*
             * Nothing picked yet: an empty outline rather than the fallback
             * wrench. The fallback is what a *saved* row with no icon falls
             * back to; drawing it here would look like a choice somebody made,
             * and this field is required precisely so that never happens.
             */
            : (
              <span
                key={size}
                aria-hidden="true"
                className="inline-block shrink-0 border border-dashed border-line-strong"
                style={{ width: size, height: size, borderRadius: Math.round(size * 0.29) }}
              />
            )
        ))}
      </div>
      <p className="text-[12.5px] leading-[1.45] text-body">
        {slug ? (
          <>
            How it looks in a list row, on a phone card and on a record header.
            <br />
            The dot is the status and comes later.
          </>
        ) : 'Pick an icon to see how it will look on a list row, a phone card and a record header.'}
      </p>
    </div>
  )
}

/**
 * One tile at one size — the shape every mark is built on.
 *
 * Radius and glyph scale with the size (0.29× and 0.57×, `list-marks.md` §1),
 * so a 56px tile is the 28px one enlarged rather than a different drawing.
 */
export function Tile({ set, slug, colour, size }: {
  set: GlyphSet
  slug: string | null
  colour: string | null
  size: number
}) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.29),
        background: markTint(colour),
        color: markInk(colour),
      }}
    >
      <Glyph
        set={set}
        slug={slug}
        size={Math.round(size * 0.57)}
        strokeWidth={size >= 44 ? 1.8 : 1.9}
      />
    </span>
  )
}
